<template>
  <div class="parent-div">
    <div>
      <v-dialog
        v-model="deleteDeskDialogOpen"
        max-width="500px"
        v-if="editMode"
      >
        <v-card class="card-delete">
          <v-card-title>{{ $t("crudDesks.deleteConfirm") }}</v-card-title>

          <v-card-actions>
            <v-spacer />
            <v-btn color="F44336" text @click="closeDeskDeleteDialog">
              {{ $t("crudCommon.cancel") }}
            </v-btn>
            <v-btn color="F44336" text @click="confirmDeskDeleteDialog">
              {{ $t("crudCommon.delete") }}
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <div>
        <div class="desk-card">
          <ul
            style="border-radius: 20px"
            v-for="desk in sortedDesks"
            :key="desk.id"
            v-bind:class="{
              'list-equipment': desk.deskType == 'EQUIPMENT',
              'list-desk': desk.deskType == 'DESK',
            }"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <li
                  style="border-radius: 20px; padding: 2px"
                  v-on="on"
                  v-bind="attrs"
                  :class="
                    isItemBookable(desk) ? 'neumorphism-out' : 'neumorphism-in'
                  "
                >
                  <div class="space-between">
                    <div>
                      {{ desk.name }}
                    </div>
                    <div v-if="editMode">
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <EditDeskDialogButton
                            :room="desk.room"
                            :desk="deskCopy"
                            @click="copyDeskOf(desk)"
                            icon
                            small
                            v-on="on"
                            aria-label="edit-desk-button"
                          >
                            <template #dialog-button-text>
                              <v-icon> mdi-pencil </v-icon>
                            </template>
                          </EditDeskDialogButton>
                        </template>
                        <span>{{ $t("crudCommon.tooltipEdit") }}</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn
                            icon
                            small
                            @click="openDeskDeleteDialog(desk)"
                            v-on="on"
                            aria-label="delete-desk-button"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("crudCommon.tooltipDelete") }}</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn
                            icon
                            small
                            @click="resizeDesk(desk)"
                            v-on="on"
                            aria-label="resize-desk-button"
                          >
                            <v-icon>mdi-resize</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("roomsList.tooltipResize") }}</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn
                            icon
                            small
                            @click="bringDeskToFront(desk)"
                            v-on="on"
                            aria-label="bring-to-front-desk-button"
                          >
                            <v-icon>mdi-arrange-bring-to-front</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("roomsList.tooltipBringToFront") }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                  <div style="display: flex; justify-content: center">
                    <img
                      style="
                        width: 210px;
                        height: fit-content;
                        border-radius: 30px;
                      "
                      src="@/assets/images/desk_picture.png"
                      alt="image d'une salle"
                      v-if="getDeskType(desk)"
                    />
                  </div>
                  <div aria-label="desk-slots" class="space-between">
                    <desk-slot-icon
                      :desk="desk"
                      :newDeskSlot="newDeskSlot(desk)"
                      :editMode="editMode"
                      v-if="desk.deskType == 'DESK'"
                    />
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <button
                          v-on="on"
                          v-if="desk.deskType == 'DESK'"
                          aria-label="sub-btn"
                        >
                          <sub-icon :resource="desk" />
                        </button>
                      </template>
                      <span v-if="isNotSubscribed(desk)">
                        {{ $t("crudDesks.subscribeToThisDesk") }}
                      </span>
                      <span v-else>
                        {{ $t("crudDesks.unsubscribeToThisDesk") }}
                      </span>
                    </v-tooltip>
                  </div>
                </li>
              </template>
              <span>{{ getBookedInfo(desk) }}</span>
            </v-tooltip>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EditDeskDialogButton from "@/components/components-salles/admin/crud/desk/EditDeskDialogButton";
import { mapActions, mapGetters } from "vuex";
import DeskSlotIcon from "@/components/components-salles/admin/details/room/DeskSlotIcon.vue";
import base64ToImage from "../../../../components-base/image/base64ToImage.vue";
import SubIcon from "./SubIcon.vue";
import { DateTime, Interval } from "luxon";

export default {
  name: "DesksList",
  components: {
    EditDeskDialogButton,
    DeskSlotIcon,
    base64ToImage,
    SubIcon,
  },
  props: {
    room: null,
    desks: null,
    planRef: null,
    deskTooltip: null,
    allDesks: null,
    editMode: null,
    availableDesks: null,
    bookInterval: null,
    bookedDesks: null,
    getDesksAvailable: null,
  },
  computed: {
    ...mapGetters("deskModule", {
      deskById: "getDeskById",
    }),
    ...mapGetters("userModule", ["getCurrentUser"]),
    sortedDesks() {
      let sortedSlots = this.allDesks;
      sortedSlots = sortedSlots.sort((a, b) => {
        let A = a.name,
          B = b.name;
        if (A < B) {
          return -1;
        }
        if (A > B) {
          return 1;
        }
        return 0;
      });
      return sortedSlots.sort();
    },
  },
  data: () => ({
    deskCopy: null,
    deleteDeskDialogOpen: false,
    deskToDelete: null,
    desksAvailable: null,
  }),
  methods: {
    ...mapActions("deskModule", ["editDesk", "deleteDesk"]),
    ...mapActions("notificationModule", ["showNotification"]),
    copyDeskOf(desk) {
      this.deskCopy = structuredClone(desk);
    },
    openDeskDeleteDialog(desk) {
      this.deskToDelete = desk;
      this.deleteDeskDialogOpen = true;
    },
    closeDeskDeleteDialog() {
      this.deleteDeskDialogOpen = false;
    },
    confirmDeskDeleteDialog() {
      this.deleteDesk(this.deskToDelete);
      this.closeDeskDeleteDialog();
    },
    resizeDesk(desk) {
      desk.x = 0;
      desk.y = 0;
      desk.w = 100;
      desk.h = 100;
      this.planRef.requestCanvasFrame();
    },
    newDeskSlot(item) {
      return {
        deskId: item.id,
      };
    },
    bringDeskToFront(desk) {
      this.planRef.bringToFront(desk);
    },
    getDeskType(item) {
      if (item.deskType.includes("DESK")) return true;
    },
    getSlots(item) {
      return this.deskSlotsByDeskId(item.id);
    },
    getBookedInfo(item) {
      const bookings = this.bookedDesks?.bookings.get(item);
      if (!bookings) return "Disponible";
      let sb = "Réservé du : ";
      bookings.forEach((booking) => {
        if (
          booking.interval.start.toLocaleString(DateTime.DATE_SHORT) ==
            booking.interval.end.toLocaleString(DateTime.DATE_SHORT) &&
          booking.interval.start.toLocaleString(
            DateTime.TIME_SIMPLE <= "09:00"
          ) &&
          booking.interval.end.toLocaleString(DateTime.TIME_SIMPLE == "18:00")
        ) {
          sb = `Réservé aujourd'hui : ${booking.interval.start.toLocaleString(
            DateTime.DATE_SHORT
          )}`;
        } else {
          sb += `${booking.interval.start.toLocaleString(
            DateTime.DATETIME_SHORT
          )} au ${booking.interval.end.toLocaleString(
            DateTime.DATETIME_SHORT
          )}`;
        }
      });
      return sb;
    },
    isItemBookable(item) {
      return !this.bookedDesks ? false : !this.bookedDesks.desks.has(item);
    },
    returnToDesksList() {
      this.$router.push({
        name: "admin-room-desks",
      });
    },
    isNotSubscribed(item) {
      return !item.subscribersIds.includes(this.getCurrentUser.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.list-equipment {
  user-select: none;
  padding: 5px;
  margin: 3;
  border-radius: 5px;
  align-content: center;
  justify-content: center;
  list-style: none;
  display: flex;
  align-items: center;
  width: 300px;
  height: 100px;
}
.list-desk {
  user-select: none;
  padding: 5px;
  margin: 3;
  border-radius: 5px;
  align-content: center;
  justify-content: center;
  list-style: none;
  width: 300px;
}

.space-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 5px;
}

.elem-text {
  display: flex;
  align-items: center;
}

.parent-div {
  margin: 10px;
  max-height: 350px;
  padding: 10px;
  justify-content: space-between;
  border-radius: 20px;
  overflow-x: auto;
  overflow-y: auto;
}
.desk-card {
  padding: 5px;
  margin: 5px;
  border-radius: 20px;
  display: inline-flex;
}
ul {
  margin: 3px;
}
</style>
